/** Production Canvas — load/error render decision (pure, headless logic)
 * (VISTOERA_WORKSPACE_TO_CANVAS_TRANSITION_ERROR_STATE_FLASH_WHITE_FRAME_AND_FIRST_PAINT_VISUAL_ATOMICITY_FIX_P0_V1).
 *
 * Extracted 1:1 from `ProductionCanvasPage.tsx`'s actual render branch so
 * the exact "should the bare error `Result` replace `CanvasBoard` this
 * render?" decision can be regression-tested headlessly (this repo's
 * existing convention — see `canvasWorkspaceTransition.node-test.ts` —
 * never mounts a React tree in its "unit tests"; every transition/state
 * invariant lives in a plain, framework-agnostic function like this one
 * instead). `ProductionCanvasPage.tsx` imports and calls this directly;
 * never re-implement the condition inline there.
 *
 * Two independent bugs this closes (task spec §9):
 *
 * - ROOT_CAUSE_ERROR_STATE_FLASH: `canvas`/`loadError`/`loading` are only
 *   ever read here for the CURRENT `projectId`'s own instance — the
 *   route element itself is mounted `key={projectId}` (see
 *   `ProductionCanvasPage`'s default export), so a project switch always
 *   produces a brand-new instance with fresh initial state
 *   (`loading=true, loadError=null, canvas=null`) before this function
 *   is ever called for it. There is structurally no "previous project's
 *   error" input this function could ever be asked to judge.
 * - The Exit-race guard (spec §5/§17/§67): while `transitionPhase !==
 *   'idle'` (a Canvas→Workspace Exit is in flight — the real
 *   `navigate()` is still up to ~260ms away per
 *   `canvasExitTransitionController`), a load failure that lands DURING
 *   that window must never replace the outgoing `CanvasBoard` with the
 *   error `Result` for the transition's last few frames — the user
 *   already chose to leave. */

export type CanvasTransitionPhaseForRender = 'idle' | 'exiting'

export interface CanvasLoadRenderInput {
  /** `getProductionCanvas` in flight for the CURRENT (fresh, per-project)
   * instance — shell-first (spec §40/§41): while true, always render
   * `CanvasBoard` in its own hydrating mode, never the error `Result`. */
  loading: boolean
  /** Non-null only when the CURRENT instance's own `load()` call rejected
   * — never carries a previous project's message (see file header). */
  loadError: string | null
  /** Non-null only once the CURRENT instance's own `load()` call
   * resolved successfully. */
  canvas: unknown | null
  /** `useCanvasWorkspaceTransition().phase` — `'exiting'` for the ~260ms
   * window between a V-logo click and the real `navigate()` firing. */
  transitionPhase: CanvasTransitionPhaseForRender
}

/** `true` ⇒ render the bare error `Result` (retry / back-to-projects);
 * `false` ⇒ render `CanvasBoard` (placeholder-backed + hydrating while
 * `loading`, the real `canvas` once it resolves). */
export function shouldShowCanvasLoadErrorSurface(input: CanvasLoadRenderInput): boolean {
  if (input.loading) return false
  if (input.transitionPhase !== 'idle') return false
  return Boolean(input.loadError) || !input.canvas
}
